import { useEffect, useState } from 'react';
import axios from 'axios';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { formatApiError } from '../../lib/utils';
import { Palette, Upload, Save } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function OperatorBranding() {
    const [branding, setBranding] = useState({
        company_name: '',
        logo_url: '',
        primary_color: '#D4AF37',
        secondary_color: '#E5D3B3',
        pdf_background_color: '#0A0D14',
        welcome_message: '',
        support_email: '',
        support_phone: '',
        whatsapp_number: '',
        website_url: ''
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchBranding();
    }, []);

    const fetchBranding = async () => {
        try {
            const response = await axios.get(`${API}/api/operator/branding`, { withCredentials: true });
            setBranding({ ...branding, ...response.data });
        } catch (error) {
            console.error('Failed to fetch branding:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await axios.put(`${API}/api/operator/branding`, branding, { withCredentials: true });
            toast.success('Branding updated successfully');
        } catch (error) {
            toast.error(formatApiError(error.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <DashboardLayout type="operator">
                <div className="flex items-center justify-center h-64">
                    <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout type="operator">
            <div className="space-y-8" data-testid="operator-branding">
                {/* Header */}
                <div>
                    <h1 className="font-outfit text-2xl lg:text-3xl font-bold text-white">White-Label Branding</h1>
                    <p className="text-muted-foreground mt-1">Customize the experience for your travelers</p>
                </div>

                <form onSubmit={handleSave} className="grid lg:grid-cols-2 gap-6">
                    {/* Company Info */}
                    <Card className="bg-[#121620] border-white/10">
                        <CardHeader>
                            <CardTitle className="text-white font-outfit flex items-center gap-2">
                                <Palette className="w-5 h-5 text-gold" />
                                Company Details
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label>Company Name</Label>
                                <Input
                                    value={branding.company_name}
                                    onChange={(e) => setBranding({ ...branding, company_name: e.target.value })}
                                    placeholder="Your Company Name"
                                    className="bg-[#0A0D14] border-white/10"
                                    data-testid="branding-company-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Logo URL</Label>
                                <Input
                                    value={branding.logo_url}
                                    onChange={(e) => setBranding({ ...branding, logo_url: e.target.value })}
                                    placeholder="https://..."
                                    className="bg-[#0A0D14] border-white/10"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Welcome Message</Label>
                                <Input
                                    value={branding.welcome_message}
                                    onChange={(e) => setBranding({ ...branding, welcome_message: e.target.value })}
                                    placeholder="Welcome to our audio guide experience!"
                                    className="bg-[#0A0D14] border-white/10"
                                />
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <Label>Primary Color</Label>
                                    <div className="flex gap-2">
                                        <Input
                                            type="color"
                                            value={branding.primary_color}
                                            onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                                            className="w-12 h-10 p-1 bg-[#0A0D14] border-white/10"
                                        />
                                        <Input
                                            value={branding.primary_color}
                                            onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                                            className="flex-1 bg-[#0A0D14] border-white/10"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Secondary Color</Label>
                                    <div className="flex gap-2">
                                        <Input
                                            type="color"
                                            value={branding.secondary_color}
                                            onChange={(e) => setBranding({ ...branding, secondary_color: e.target.value })}
                                            className="w-12 h-10 p-1 bg-[#0A0D14] border-white/10"
                                        />
                                        <Input
                                            value={branding.secondary_color}
                                            onChange={(e) => setBranding({ ...branding, secondary_color: e.target.value })}
                                            className="flex-1 bg-[#0A0D14] border-white/10"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>PDF Background</Label>
                                    <div className="flex gap-2">
                                        <Input
                                            type="color"
                                            value={branding.pdf_background_color || '#0A0D14'}
                                            onChange={(e) => setBranding({ ...branding, pdf_background_color: e.target.value })}
                                            className="w-12 h-10 p-1 bg-[#0A0D14] border-white/10"
                                            data-testid="pdf-bg-color-input"
                                        />
                                        <Input
                                            value={branding.pdf_background_color || '#0A0D14'}
                                            onChange={(e) => setBranding({ ...branding, pdf_background_color: e.target.value })}
                                            className="flex-1 bg-[#0A0D14] border-white/10"
                                        />
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Contact Info */}
                    <Card className="bg-[#121620] border-white/10">
                        <CardHeader>
                            <CardTitle className="text-white font-outfit">Contact Information</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label>Support Email</Label>
                                <Input
                                    type="email"
                                    value={branding.support_email}
                                    onChange={(e) => setBranding({ ...branding, support_email: e.target.value })}
                                    placeholder="support@company.com"
                                    className="bg-[#0A0D14] border-white/10"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Support Phone</Label>
                                <Input
                                    value={branding.support_phone}
                                    onChange={(e) => setBranding({ ...branding, support_phone: e.target.value })}
                                    placeholder="+1 234 567 8900"
                                    className="bg-[#0A0D14] border-white/10"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>WhatsApp Number</Label>
                                <Input
                                    value={branding.whatsapp_number}
                                    onChange={(e) => setBranding({ ...branding, whatsapp_number: e.target.value })}
                                    placeholder="+1 234 567 8900"
                                    className="bg-[#0A0D14] border-white/10"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Website URL</Label>
                                <Input
                                    value={branding.website_url}
                                    onChange={(e) => setBranding({ ...branding, website_url: e.target.value })}
                                    placeholder="https://www.yourcompany.com"
                                    className="bg-[#0A0D14] border-white/10"
                                />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Preview */}
                    <Card className="lg:col-span-2 bg-[#121620] border-white/10">
                        <CardHeader>
                            <CardTitle className="text-white font-outfit">Preview</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div 
                                className="p-6 rounded-md border border-white/10"
                                style={{ backgroundColor: branding.primary_color + '10' }}
                            >
                                <div className="flex items-center gap-3 mb-4">
                                    {branding.logo_url ? (
                                        <img src={branding.logo_url} alt="Logo" className="w-10 h-10 rounded-md object-contain" />
                                    ) : (
                                        <div 
                                            className="w-10 h-10 rounded-md flex items-center justify-center"
                                            style={{ backgroundColor: branding.primary_color }}
                                        >
                                            <span className="text-white font-bold">{branding.company_name?.charAt(0) || 'A'}</span>
                                        </div>
                                    )}
                                    <span className="font-outfit font-semibold text-white">{branding.company_name || 'Your Company'}</span>
                                </div>
                                <p className="text-muted-foreground">{branding.welcome_message || 'Welcome to our audio guide experience!'}</p>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Save Button */}
                    <div className="lg:col-span-2 flex justify-end">
                        <Button 
                            type="submit" 
                            className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2"
                            disabled={saving}
                            data-testid="save-branding-btn"
                        >
                            <Save className="w-4 h-4" />
                            {saving ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </div>
                </form>
            </div>
        </DashboardLayout>
    );
}
